import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, Badge, Chip, Stack,
  IconButton, Tooltip, Divider, Menu, MenuItem, ListItemIcon,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloudIcon from '@mui/icons-material/Cloud';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import GrainIcon from '@mui/icons-material/Grain';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';

import NotificationBell from './NotificationBell';
import { useGetSingleEmployeeDetails } from '../../hooks/useEmployees';
import { useEmployeeCycleAttendance } from '../../hooks/useAttendance';
import { getCurrentCycle } from '../../utils/attendanceCycle';
import { getMediaUrl } from '../../utils/urlHelper';
import { getInitials } from '../../utils/initials';
import { formatDate } from '../../utils/dateFormat';
import { AuthContext } from '../../context/logeduser';

const ACCENT = '#2f6bff';
const NUM_FONT = "'Space Grotesk', 'Outfit', sans-serif";
const pad2 = (n) => String(n).padStart(2, '0');
const todayISO = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
};

// Map an Open-Meteo WMO weather code to a label + icon + soft tile colours.
const weatherMeta = (code) => {
  if (code === 0) return { label: 'Clear', Icon: WbSunnyIcon, color: '#f59e0b', bg: '#fffbeb' };
  if (code === 1 || code === 2) return { label: 'Partly cloudy', Icon: FilterDramaIcon, color: '#60a5fa', bg: '#eff6ff' };
  if (code === 3) return { label: 'Cloudy', Icon: CloudIcon, color: '#94a3b8', bg: '#f1f5f9' };
  if (code === 45 || code === 48) return { label: 'Fog', Icon: CloudIcon, color: '#94a3b8', bg: '#f1f5f9' };
  if (code >= 51 && code <= 67) return { label: 'Rain', Icon: GrainIcon, color: '#3b82f6', bg: '#eff6ff' };
  if (code >= 71 && code <= 77) return { label: 'Snow', Icon: AcUnitIcon, color: '#60a5fa', bg: '#eff6ff' };
  if (code >= 80 && code <= 82) return { label: 'Showers', Icon: GrainIcon, color: '#3b82f6', bg: '#eff6ff' };
  if (code >= 95) return { label: 'Thunderstorm', Icon: ThunderstormIcon, color: '#7c3aed', bg: '#f5f3ff' };
  return { label: 'Cloudy', Icon: CloudIcon, color: '#60a5fa', bg: '#eff6ff' };
};

/**
 * The employee dashboard top bar (profile strip). Rendered once by the employee
 * layout (ResponsiveDrawer) so it appears on every employee page. Self-fetches
 * its own data — nothing is threaded in from the page it sits above.
 */
const EmployeeTopNav = () => {
  const navigate = useNavigate();
  const { authData, logout } = useContext(AuthContext);
  const employeeId = authData?.employee_no;

  const [showCtc, setShowCtc] = useState(false);
  const [clock, setClock] = useState({ day: '', date: '', time: '' });
  const [weather, setWeather] = useState(null); // { temp, code, city } from Open-Meteo
  const [profileAnchor, setProfileAnchor] = useState(null);

  const currentCycleParam = useMemo(() => getCurrentCycle().cycle, []);

  const handleLogout = () => {
    setProfileAnchor(null);
    if (typeof logout === 'function') logout();
    navigate('/login');
  };

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClock({
        day: now.toLocaleDateString('en-GB', { weekday: 'long' }),
        date: formatDate(now),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      });
    };
    tick();
    const t = setInterval(tick, 1000 * 30);
    return () => clearInterval(t);
  }, []);

  // Real-time weather for the VIEWER's location (browser Geolocation → Open-Meteo,
  // both key-free + CORS-enabled). The office (Mahadevapura) loads instantly and is
  // the fallback when the user blocks/ignores the prompt or geolocation is
  // unavailable; on grant it upgrades to the user's own city. Refreshed every 30 min.
  useEffect(() => {
    let cancelled = false;
    // Office coordinates — shown immediately and kept if geolocation is denied.
    let coords = { lat: 12.9908, lon: 77.6920, city: 'Mahadevapura' };

    const loadWeather = async () => {
      try {
        const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,weather_code&timezone=auto`);
        const json = await res.json();
        if (!cancelled && json?.current) {
          setWeather({ temp: Math.round(json.current.temperature_2m), code: json.current.weather_code, city: coords.city });
        }
      } catch (e) { /* offline — the tile keeps its default look */ }
    };

    // Reverse-geocode coords → a city name (BigDataCloud: free, key-less, CORS-ok).
    const cityFromCoords = async (lat, lon) => {
      try {
        const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`);
        const j = await res.json();
        return j.city || j.locality || j.principalSubdivision || 'Your location';
      } catch { return 'Your location'; }
    };

    loadWeather(); // instant office weather
    const t = setInterval(loadWeather, 30 * 60 * 1000);

    // Upgrade to the user's real location once granted (no-op if blocked/unsupported).
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          const { latitude, longitude } = pos.coords;
          const city = await cityFromCoords(latitude, longitude);
          if (cancelled) return;
          coords = { lat: latitude, lon: longitude, city };
          loadWeather();
        },
        () => { /* denied / unavailable — keep the office weather */ },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 },
      );
    }

    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const { data: employeeData } = useGetSingleEmployeeDetails(employeeId);
  // Current cycle drives the check-in pill (today's punch) + the present streak.
  const { data: currentCycle } = useEmployeeCycleAttendance(employeeId, { cycle: currentCycleParam });

  const emp = employeeData?.employee || employeeData;
  const ctcItems = employeeData?.ctc_items || [];
  const todayCell = currentCycle?.days?.[todayISO()];

  // Present streak: walk backward from today, holidays skipped, absent/leave/no-record breaks.
  const streak = useMemo(() => {
    const days = currentCycle?.days || {};
    const dates = currentCycle?.dates || [];
    let st = 0;
    for (let i = dates.length - 1; i >= 0; i--) {
      const iso = dates[i];
      if (iso > todayISO()) continue;
      const status = days[iso]?.status;
      if (['PRESENT', 'ON_DUTY', 'HALF_DAY'].includes(status)) st += 1;
      else if (status === 'HOLIDAY') continue;
      else break;
    }
    return st;
  }, [currentCycle]);

  const designation = emp?.designation || 'N/A';
  const workMode = emp?.attendance_schema || 'On-site';
  const wm = weather ? weatherMeta(weather.code) : null;
  const WIcon = wm?.Icon || CloudIcon;

  return (
    <Paper elevation={0} sx={{ borderRadius: '16px', bgcolor: '#fff', border: '1px solid #eef0f4', boxShadow: '0 4px 16px rgba(17,24,39,0.05)', mb: { xs: 1.5, md: 2.5 }, p: { xs: 1.5, sm: 1.75 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2.5 } }}>
        {/* Identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Tooltip title="Account">
            <Box onClick={(e) => setProfileAnchor(e.currentTarget)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={<Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#22c55e', border: '2px solid #fff' }} />}
              >
                <Avatar src={getMediaUrl(emp?.photo)} sx={{ width: 46, height: 46, bgcolor: ACCENT, fontWeight: 700, fontSize: '1.1rem', border: '2px solid #e0e7ff' }}>
                  {getInitials(emp?.name)}
                </Avatar>
              </Badge>
            </Box>
          </Tooltip>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '14px', minWidth: 190, boxShadow: '0 12px 32px rgba(17,24,39,0.12)', mt: 1 } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }} noWrap>{emp?.name || 'Employee'}</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>{emp?.employee_no || ''}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setProfileAnchor(null); navigate('/profile'); }} sx={{ py: 1.1 }}>
              <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ py: 1.1, color: '#dc2626' }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
              Log Out
            </MenuItem>
          </Menu>
          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{ minWidth: 0, cursor: 'pointer' }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#111827', lineHeight: 1.2 }} noWrap>
                {emp?.name || 'Employee'}
              </Typography>
              <KeyboardArrowDownIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.6 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#8b5cf6', ml: '8px !important' }} />}
                label={designation}
                sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#f5f3ff', color: '#6d28d9', '& .MuiChip-icon': { mr: '-2px' } }}
              />
              {streak > 0 && (
                <Chip size="small" icon={<LocalFireDepartmentIcon sx={{ fontSize: 16, color: '#f59e0b !important' }} />} label={streak} sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#fff7ed', color: '#c2410c' }} />
              )}
            </Stack>
          </Box>
        </Box>

        {/* Weather + day/date (live temp + condition + city via Open-Meteo) */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.25, pl: { md: 1 }, borderLeft: { md: '1px solid #eef0f4' }, ml: { md: 1 } }}>
          <Tooltip arrow title={wm ? `${wm.label}${weather?.city ? ` · ${weather.city}` : ''}` : ''}>
            <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: wm?.bg || '#eff6ff', color: wm?.color || '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WIcon fontSize="small" />
            </Box>
          </Tooltip>
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#111827', lineHeight: 1.1 }}>
              {clock.day}{weather ? ` · ${weather.temp}°C` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {clock.date}{weather?.city ? ` · ${weather.city}` : (wm ? ` · ${wm.label}` : '')}
            </Typography>
          </Box>
        </Box>

        {/* Right pills */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ ml: { md: 'auto' } }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #eef0f4' }}>
            <WorkOutlineIcon sx={{ color: '#6b7280', fontSize: 16 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', lineHeight: 1, fontSize: '0.62rem' }}>Shift</Typography>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.85rem', lineHeight: 1.2 }}>{workMode}</Typography>
            </Box>
          </Box>

          <Box onClick={() => navigate('/payslip')} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6, borderRadius: '12px', bgcolor: '#f0fdf4', border: '1px solid #dcfce7', cursor: 'pointer' }}>
            <AccountBalanceWalletIcon sx={{ color: '#16a34a', fontSize: 18 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', lineHeight: 1, fontSize: '0.62rem' }}>Monthly CTC</Typography>
              <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, color: '#111827', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {showCtc ? `₹${ctcItems?.[0]?.monthly_ctc ?? '0'}` : '₹ •••••'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowCtc((v) => !v); }} sx={{ color: '#16a34a' }}>
              {showCtc ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6, borderRadius: '12px', background: todayCell?.in_time ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#3b82f6,#2f6bff)', color: '#fff', minWidth: 108 }}>
            <AccessTimeIcon sx={{ fontSize: 18 }} />
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1, fontSize: '0.62rem' }}>
                {todayCell?.in_time ? 'Checked in' : 'Current time'}
              </Typography>
              <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2 }}>
                {todayCell?.in_time || clock.time}
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ width: 36, height: 36, borderRadius: '11px', border: '1px solid #eef0f4', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NotificationBell />
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default EmployeeTopNav;
